// src/pages/Budget/BudgetVsActualView.jsx
import React, { useEffect, useState, useMemo } from "react";
import T from '../../components/common/T';
import { useBudgets } from "../../context/BudgetContext";
import { useAuth } from "../../context/AuthContext";
import DataTable from "../../components/common/DataTables.jsx";
import PageHeader from "../../components/common/PageHeader";
import { 
  CurrencyDollarIcon, 
  ChartBarIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowLeftIcon 
} from "@heroicons/react/24/outline";

// --- Utility: Currency Formatter ---
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", { 
    style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount || 0);

// --- Component: Status Badge ---
const StatusBadge = ({ status }) => {
  const styles = {
    "On Track": "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/60",
    "Under Budget": "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/60",
    "Critical": "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/60",
    "Over Budget": "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/60",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase border whitespace-nowrap ${styles[status] || styles["On Track"]}`}>
      {status}
    </span>
  );
};

// --- Component: Full Page Project Detail View ---
const ProjectDetailView = ({ project, onBack }) => {
  if (!project) return null;

  const categories = [
    { label: "Labor", key: "labor", color: "bg-purple-500", iconColor: "text-purple-600 dark:text-purple-400", bg: "bg-purple-50 dark:bg-purple-950/40" },
    { label: "Materials", key: "material", color: "bg-blue-500", iconColor: "text-blue-600 dark:text-blue-400", bg: "bg-blue-50 dark:bg-blue-950/40" },
    { label: "Equipment", key: "equipment", color: "bg-amber-500", iconColor: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-950/40" },
    { label: "Operational", key: "operational", color: "bg-gray-500", iconColor: "text-gray-600 dark:text-slate-400", bg: "bg-gray-50 dark:bg-slate-800" },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Detail View Header */}
      <div className="flex justify-between items-center bg-white dark:bg-[#161b22] p-5 rounded-xl border border-gray-200 dark:border-[#30363d] shadow-sm transition-colors">
        <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 border border-gray-200 dark:border-[#30363d] transition">
                <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
                <h1 className="text-base font-bold text-gray-900 dark:text-white">{project.projectName}</h1>
                <p className="text-sm text-gray-500 dark:text-slate-400">{project.clientName} • <span className="font-medium text-blue-600 dark:text-blue-400">Financial Details</span></p>
            </div>
        </div>
        <StatusBadge status={project.status} />
      </div>

      {/* Top Level Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-[#161b22] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] transition-colors">
              <p className="text-xs font-bold uppercase text-gray-400 dark:text-slate-400">Total Budget</p>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mt-1">{formatCurrency(project.totalBudget)}</h3>
          </div>
          <div className="bg-white dark:bg-[#161b22] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] transition-colors">
              <p className="text-xs font-bold uppercase text-gray-400 dark:text-slate-400">Actual Spent</p>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mt-1">{formatCurrency(project.totalSpent)}</h3>
          </div>
          <div className={`p-6 rounded-xl shadow-sm border transition-colors ${project.variance >= 0 ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-100 dark:border-emerald-800/50' : 'bg-red-50 dark:bg-red-950/30 border-red-100 dark:border-red-800/50'}`}>
              <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-xs font-bold uppercase ${project.variance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>Net Variance</p>
                    <h3 className={`text-base font-bold mt-1 ${project.variance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                        {formatCurrency(Math.abs(project.variance))}
                    </h3>
                  </div>
                  <span className={`text-lg font-bold ${project.variance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {project.variancePercent.toFixed(1)}%
                  </span>
              </div>
              <p className="text-xs mt-2 opacity-80 font-medium">
                  {project.variance >= 0 ? "Under Budget (Surplus)" : "Over Budget (Deficit)"}
              </p>
          </div>
      </div>

      {/* Detailed Cost Breakdown Grid */}
      <div className="bg-white dark:bg-[#161b22] rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] p-6 transition-colors">
        <h3 className="text-base font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-6">
            <ChartBarIcon className="w-6 h-6 text-gray-400 dark:text-slate-500"/> Cost Breakdown
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {categories.map((cat) => {
                const budget = project[`${cat.key}Budget`] || 0;
                const spent = project[`${cat.key}Spent`] || 0;
                const percent = budget > 0 ? (spent / budget) * 100 : 0;
                const isOver = spent > budget;
                const variance = budget - spent;

                return (
                    <div key={cat.key} className="p-5 rounded-xl border border-gray-100 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#21262d]/50 hover:bg-white dark:hover:bg-[#21262d] hover:shadow-md transition duration-200">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${cat.bg} ${cat.iconColor}`}><ChartBarIcon className="w-5 h-5" /></div>
                                <span className="font-bold text-gray-700 dark:text-white text-lg capitalize">{cat.label}</span>
                            </div>
                            <span className={`text-xs font-bold px-3 py-1 rounded-full ${isOver ? 'bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400' : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300'}`}>
                                {percent.toFixed(0)}% Used
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-slate-800 rounded-full h-3 mb-5 overflow-hidden">
                            <div className={`h-3 rounded-full transition-all duration-1000 ${isOver ? 'bg-red-500' : cat.color}`} style={{ width: `${Math.min(percent, 100)}%` }}></div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 border-t border-gray-200 dark:border-slate-700 pt-4">
                            <div><span className="block text-[10px] text-gray-400 dark:text-slate-400 uppercase font-bold tracking-wider">Budget</span><span className="text-sm font-bold text-gray-800 dark:text-slate-200">{formatCurrency(budget)}</span></div>
                            <div className="text-center"><span className="block text-[10px] text-gray-400 dark:text-slate-400 uppercase font-bold tracking-wider">Actual</span><span className="text-sm font-bold text-gray-800 dark:text-slate-200">{formatCurrency(spent)}</span></div>
                            <div className="text-right"><span className="block text-[10px] text-gray-400 dark:text-slate-400 uppercase font-bold tracking-wider">Variance</span><span className={`text-sm font-bold ${variance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{formatCurrency(variance)}</span></div>
                        </div>
                    </div>
                );
            })}
        </div>
      </div>
    </div>
  );
};

const BudgetVsActualView = ({ search: propSearch = "", filter: propFilter = "All" }) => {
  const { budgetVsActual, fetchBudgetVsActual, loading } = useBudgets();
  const { checkAccess } = useAuth();
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [localSearch, setLocalSearch] = useState(propSearch);
  const [localFilter, setLocalFilter] = useState(propFilter);

  useEffect(() => { fetchBudgetVsActual(); }, [fetchBudgetVsActual]);
  useEffect(() => { setLocalSearch(propSearch); }, [propSearch]);
  useEffect(() => { setLocalFilter(propFilter); }, [propFilter]);

  const filteredData = useMemo(() => {
    return (budgetVsActual || []).filter((item) => {
      const matchesSearch = item.projectName.toLowerCase().includes(localSearch.toLowerCase());
      const matchesFilter = localFilter === "All" || item.status === localFilter;
      return matchesSearch && matchesFilter;
    });
  }, [budgetVsActual, localSearch, localFilter]);

  const stats = useMemo(() => {
    const totalBudget = filteredData.reduce((acc, curr) => acc + (curr.totalBudget || 0), 0);
    const totalSpent = filteredData.reduce((acc, curr) => acc + (curr.totalSpent || 0), 0);
    const totalVariance = totalBudget - totalSpent;
    return { totalBudget, totalSpent, totalVariance };
  }, [filteredData]);

  if (!checkAccess('financial.view')) {
    return <div className="p-10 text-center text-red-500"><T>Access Denied</T></div>;
  }

  if (loading) return <div className="p-10 text-center text-gray-500"><T>Loading Analysis...</T></div>;

  if (selectedProject) {
      return <ProjectDetailView project={selectedProject} onBack={() => setSelectedProject(null)} />;
  }

  const columns = [
    {
      header: "Project",
      accessor: "projectName",
      sortable: true,
      render: (item) => (
        <div>
            <div className="font-bold text-gray-900 dark:text-white truncate max-w-[200px]" title={item.projectName}>{item.projectName}</div>
            <div className="text-[10px] text-gray-500 dark:text-slate-400 truncate max-w-[200px]">{item.clientName}</div>
        </div>
      )
    },
    { header: "Status", accessor: "status", align: "center", sortable: true, render: (item) => <StatusBadge status={item.status} /> },
    {
      header: "Utilization",
      render: (item) => {
        const percentUsed = item.totalBudget > 0 ? (item.totalSpent / item.totalBudget) * 100 : 0;
        return (
            <div className="min-w-[150px]">
                <div className="flex justify-end text-[10px] text-gray-500 dark:text-slate-400 font-medium mb-1">
                    <span>{percentUsed.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                    <div className={`h-1.5 rounded-full ${percentUsed > 100 ? 'bg-red-500' : 'bg-blue-600 dark:bg-blue-500'}`} style={{ width: `${Math.min(percentUsed, 100)}%` }}></div>
                </div>
            </div>
        );
      }
    },
    { header: "Budget", accessor: "totalBudget", align: "right", sortable: true, render: (item) => <span className="font-medium text-gray-700 dark:text-slate-300">{formatCurrency(item.totalBudget)}</span> },
    { header: "Spent", accessor: "totalSpent", align: "right", sortable: true, render: (item) => <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(item.totalSpent)}</span> },
    { header: "Variance", accessor: "variance", align: "right", sortable: true, render: (item) => <span className={`font-bold ${item.variance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{formatCurrency(item.variance)}</span> },
    {
      header: "View",
      align: "center",
      render: (item) => (
        <button onClick={() => setSelectedProject(item)} className="p-1.5 bg-blue-50 dark:bg-blue-950/50 border border-blue-100 dark:border-blue-800/50 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/60 transition" title="View Details">
            <EyeIcon className="w-3.5 h-3.5" />
        </button>
      )
    }
  ];

  return (
    <div className="flex flex-col h-full">
      {/* DASHBOARD TOP STATS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] flex items-center gap-3 transition-colors">
            <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/50 rounded-lg text-indigo-600 dark:text-indigo-400"><CurrencyDollarIcon className="w-5 h-5"/></div>
            <div>
                <p className="text-[10px] font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wide">Total Budget</p>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{formatCurrency(stats.totalBudget)}</h3>
            </div>
        </div>
        <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] flex items-center gap-3 transition-colors">
            <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 rounded-lg text-blue-600 dark:text-blue-400"><ChartBarIcon className="w-5 h-5"/></div>
            <div>
                <p className="text-[10px] font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wide">Actual Spent</p>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{formatCurrency(stats.totalSpent)}</h3>
            </div>
        </div>
        <div className={`p-4 rounded-xl shadow-sm border flex items-center gap-3 transition-colors ${stats.totalVariance >= 0 ? 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/30' : 'border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/30'}`}>
            <div className={`p-2.5 rounded-lg ${stats.totalVariance >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400' : 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400'}`}>
                {stats.totalVariance >= 0 ? <CheckCircleIcon className="w-5 h-5"/> : <ExclamationTriangleIcon className="w-5 h-5"/>}
            </div>
            <div>
                <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Net Variance</p>
                <h3 className={`text-base font-bold ${stats.totalVariance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                    {formatCurrency(Math.abs(stats.totalVariance))}
                    <span className="text-[9px] font-semibold ml-1 uppercase opacity-80">{stats.totalVariance >= 0 ? "Surplus" : "Deficit"}</span>
                </h3>
            </div>
        </div>
      </div>

      {/* COMPACT TOOLBAR */}
      <div className="bg-[#f4f5f7] dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] border-b-0 p-2 flex items-center justify-between transition-colors">
        <div className="flex gap-2">
            <input 
                type="text" 
                placeholder="Search projects..." 
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                className="h-7 text-[11px] px-2 border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 w-64 shadow-inner outline-none focus:border-blue-500" 
            />
            <select 
                value={localFilter} 
                onChange={(e) => setLocalFilter(e.target.value)}
                className="h-7 text-[11px] px-2 border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white outline-none"
            >
                <option value="All">All Statuses</option>
                <option value="On Track">On Track</option>
                <option value="Under Budget">Under Budget</option>
                <option value="Over Budget">Over Budget</option>
                <option value="Critical">Critical</option>
            </select>
        </div>
      </div>

      {/* LIST VIEW */}
      <div className="flex-1">
        <DataTable data={filteredData} columns={columns} emptyMessage="No projects match your criteria." />
      </div>
    </div>
  );
};

export default BudgetVsActualView;